import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Stack,
    Typography,
    IconButton,
    Collapse,
    InputAdornment,
    Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Clear as ClearIcon, QrCodeScanner } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { debounce } from 'lodash';
import storeService from '../services/storeService';
import QRCodeScanner from './QRCodeScanner';
import { useFeatureConfig } from '../hooks/useFeatureConfig';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const GiftcardFilter = ({ onFilterChange }) => {
    // Feature configuration
    const { qrCodeSupported, loaded } = useFeatureConfig();
    
    
    // Filter visibility state
    const [expanded, setExpanded] = useState(false);
    
    // Store list state
    const [stores, setStores] = useState([]);
    const [selectedStores, setSelectedStores] = useState([]);
    const [includeNotSold, setIncludeNotSold] = useState(true);

    // Date range states
    const [soldDateStart, setSoldDateStart] = useState(null);
    const [soldDateEnd, setSoldDateEnd] = useState(null);
    const [activationDateStart, setActivationDateStart] = useState(null);
    const [activationDateEnd, setActivationDateEnd] = useState(null);

    // Text search states
    const [giftcardNoSearch, setGiftcardNoSearch] = useState('');
    const [soldBySearch, setSoldBySearch] = useState('');
    
    // QR Scanner state
    const [qrScannerOpen, setQrScannerOpen] = useState(false);

    // Load stores and set default selection (all stores)
    useEffect(() => {
        const loadStores = async () => {
            try {
                const response = await storeService.getStores();
                setStores(response);
                // Set all stores as selected by default
                setSelectedStores(response.map(store => store.StoreId));
            } catch (error) {
                console.error('Failed to load stores:', error);
            }
        };
        loadStores();
    }, []);

    // Debounced filter updates
    const debouncedFilterUpdate = debounce((filters) => {
        onFilterChange(filters);
    }, 300);

    // Custom DatePicker with clear button
    const DatePickerWithClear = ({ label, value, onChange, ...props }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DesktopDatePicker
                label={label}
                value={value}
                onChange={onChange}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                {...props}
            />
            {value && (
                <IconButton
                    size="small"
                    onClick={() => onChange(null)}
                    sx={{ color: 'text.secondary' }}
                    aria-label={`Clear ${label}`}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );

    // Update filters when any value changes
    useEffect(() => {
        // Format dates as local date strings (YYYY-MM-DD) to prevent timezone conversion
        const formatLocalDate = (date) => {
            if (!date) return null;
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const filters = {
            storeIds: selectedStores.length > 0 ? selectedStores.join(',') : '',
            includeNotSold: includeNotSold.toString(),
            giftcardNoSearch: giftcardNoSearch.length >= 3 ? giftcardNoSearch : null,
            soldBySearch: soldBySearch.length >= 3 ? soldBySearch : null,
            soldDateStart: formatLocalDate(soldDateStart),
            soldDateEnd: formatLocalDate(soldDateEnd),
            activationDateStart: formatLocalDate(activationDateStart),
            activationDateEnd: formatLocalDate(activationDateEnd),
        };
        debouncedFilterUpdate(filters);
    }, [
        selectedStores,
        includeNotSold,
        giftcardNoSearch,
        soldBySearch,
        soldDateStart,
        soldDateEnd,
        activationDateStart,
        activationDateEnd,
    ]);

    // QR Scanner handlers
    const handleQrScanSuccess = (qrData) => {
        setGiftcardNoSearch(qrData);
        setQrScannerOpen(false);
    };

    const handleOpenQrScanner = () => {
        setQrScannerOpen(true);
    };

    const handleCloseQrScanner = () => {
        setQrScannerOpen(false);
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            {/* Filter Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ mr: 1 }}>
                    Filters
                </Typography>
                <IconButton
                    onClick={() => setExpanded(!expanded)}
                    aria-label={expanded ? 'collapse filters' : 'expand filters'}
                    size="small"
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Text Search Fields */}
                        <TextField
                            label="Gift Card No"
                            value={giftcardNoSearch}
                            onChange={(e) => setGiftcardNoSearch(e.target.value)}
                            helperText="Type 3+ characters to search"
                            InputProps={{
                                endAdornment: (loaded && qrCodeSupported) ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleOpenQrScanner}
                                            edge="end"
                                            color="primary"
                                            title="Scan QR Code"
                                            size="small"
                                        >
                                            <QrCodeScanner />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        
                        {/* Store Selection */}
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Stores</InputLabel>
                            <Select
                                multiple
                                value={selectedStores}
                                onChange={(e) => setSelectedStores(e.target.value)}
                                input={<OutlinedInput label="Stores" />}
                                renderValue={(selected) => {
                                    const selectedNames = stores
                                        .filter(store => selected.includes(store.StoreId))
                                        .map(store => store.StoreName)
                                        .join(', ');
                                    return selectedNames || 'All Stores';
                                }}
                                MenuProps={MenuProps}
                            >
                                {stores.map((store) => (
                                    <MenuItem key={store.StoreId} value={store.StoreId}>
                                        <Checkbox checked={selectedStores.indexOf(store.StoreId) > -1} />
                                        <ListItemText primary={store.StoreName} />
                                    </MenuItem>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <MenuItem>
                                    <Checkbox
                                        checked={includeNotSold}
                                        onChange={(e) => setIncludeNotSold(e.target.checked)}
                                    />
                                    <ListItemText primary="Not Sold" />
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Sold By"
                            value={soldBySearch}
                            onChange={(e) => setSoldBySearch(e.target.value)}
                            helperText="Type 3+ characters to search"
                        />
                    </Box>

                {/* Date Ranges */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <span>Sold Date:</span>
                        <DatePickerWithClear
                            label="From"
                            value={soldDateStart}
                            onChange={setSoldDateStart}
                        />
                        <DatePickerWithClear
                            label="To"
                            value={soldDateEnd}
                            onChange={setSoldDateEnd}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <span>Activation Date:</span>
                        <DatePickerWithClear
                            label="From"
                            value={activationDateStart}
                            onChange={setActivationDateStart}
                        />
                        <DatePickerWithClear
                            label="To"
                            value={activationDateEnd}
                            onChange={setActivationDateEnd}
                        />
                    </Box>
                </Box>
                </Stack>
            </Collapse>

            {/* QR Code Scanner Dialog */}
            {loaded && qrCodeSupported && (
                <QRCodeScanner
                    open={qrScannerOpen}
                    onClose={handleCloseQrScanner}
                    onScanSuccess={handleQrScanSuccess}
                />
            )}
        </Paper>
    );
};

export default GiftcardFilter;
